import { maxBy } from 'lodash-es';
import { useCallback, useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmButton } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { CancellableFormFooter } from 'src/modals/common';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import {
  SeatingAllocationInputTab,
  SeatingAllocationInputV2,
} from 'src/modals/ConfirmSale/SeatingAllocationInput';
import { ModalBody, ModalFooter, ModalProps } from 'src/modals/Modal';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { isSuccess } from 'src/utils/errorUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { getPosManagedMarketplaceSaleInputFromSale } from 'src/utils/saleUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  Marketplace,
  PointOfSaleSaleStatus,
  PosUiActionResult,
  SaleActionType,
  SaleClient,
  SaleDetails,
  Seating,
} from 'src/WebApiController';

import { Summary } from '../common/Summary';
import {
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
} from '../Modal/Modal.styled';
import { FlattenedSeatingAllocation } from './SeatingAllocationInput/SeatingSingleTicketAllocationInput';

export type ConfirmSaleProps = {
  cancelTo?: ModalProps;
  action:
    | SaleActionType.Confirm
    | SaleActionType.Allocate
    | SaleActionType.ConfirmAllocation;
};

export const ConfirmSaleModal = (props: ConfirmSaleProps) => {
  const { loadingState } = useEventItemLoadingDisplay<SaleDetails>(
    FormatContentId.LoadingSaleId,
    FormatContentId.SearchingForSaleId,
    FormatContentId.CouldNotFindSaleId
  );

  return loadingState ? loadingState : <ConfirmSaleModalContent {...props} />;
};

const ConfirmSaleModalContent = ({ cancelTo, action }: ConfirmSaleProps) => {
  const {
    event,
    posEntity: sale,
    setActivePosEntity,
  } = useActivePosEntityContext<SaleDetails>();

  const { showErrorDialog, genericError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setModal, closeModal } = useContext(ModalContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState<SeatingAllocationInputTab>(
    SeatingAllocationInputTab.Listings
  );

  const [showSectionRowChangeWarning, setShowSectionRowChangeWarning] =
    useState(false);

  const [showAdminHoldWarning, setShowAdminHoldWarning] = useState(false);

  const [hasAdminHoldInListingAllocation, setHasAdminHoldInListingAllocation] =
    useState(false);

  // Bundled condition for showing any warning
  const showAnyWarning = showSectionRowChangeWarning || showAdminHoldWarning;

  const methods = useForm<SaleDetails>({
    defaultValues: sale!,
  });

  const { isDirty } = methods.formState;
  const seating = methods.watch('seating');

  const [selectedSeatings, setSelectedSeatings] = useState<
    FlattenedSeatingAllocation[]
  >([]);

  const hasAdminHoldTickets = useCallback(
    (selectedTickets: FlattenedSeatingAllocation[]): boolean => {
      if (activeTab === SeatingAllocationInputTab.Tickets) {
        // For single ticket selection, check the selected tickets directly
        return selectedTickets.some(
          (ticket) => ticket.isListingOnAdminHold === true
        );
      } else {
        // For listing selection, check the state that will be set by the allocation input component
        return hasAdminHoldInListingAllocation;
      }
    },
    [activeTab, hasAdminHoldInListingAllocation]
  );

  const createSeatingFromSelectedTickets = (
    selectedTickets: FlattenedSeatingAllocation[]
  ): Seating => {
    // Find the most frequent section and row
    const sectionCounts: Record<string, number> = {};
    const rowCounts: Record<string, number> = {};

    selectedTickets.forEach((ticket) => {
      const { section, row } = ticket;
      sectionCounts[section] = (sectionCounts[section] || 0) + 1;
      rowCounts[row || ''] = (rowCounts[row || ''] || 0) + 1;
    });

    const mostFrequentSection = maxBy(
      Object.entries(sectionCounts),
      ([_, value]) => value
    )?.[0];

    const mostFrequentRow = maxBy(
      Object.entries(rowCounts),
      ([_, value]) => value
    )?.[0];

    // SeatFr & SeatTo sort by listingOrd or seat text
    let sortedTickets: FlattenedSeatingAllocation[] = [];

    const allHaveListingOrd = selectedTickets.every(
      (ticket) => ticket.listingOrd != null
    );

    if (allHaveListingOrd) {
      sortedTickets = selectedTickets.toSorted(
        (a, b) => (a.listingOrd || 0) - (b.listingOrd || 0)
      );
    } else {
      sortedTickets = selectedTickets.toSorted((a, b) =>
        (a.seat || '').localeCompare(b.seat || '', undefined, {
          numeric: true,
        })
      );
    }

    const seatFr = sortedTickets[0]?.seat || null;
    const seatTo = sortedTickets[sortedTickets.length - 1]?.seat || null;

    // Collect ticketIds & listingIds
    const ticketIds = selectedTickets.map((ticket) => ticket.id);
    const listingIds = selectedTickets.reduce<number[]>((ids, ticket) => {
      if (ticket.listingId != null) {
        ids.push(ticket.listingId);
      }
      return ids;
    }, []);

    const seating: Seating = {
      section: mostFrequentSection || '',
      sectionId: null,
      row: mostFrequentRow || null,
      rowId: null,
      seatFr,
      seatTo,
      ticketIds,
      listingIds,
    };

    return seating;
  };

  const onSubmit = useCallback(
    async (saleForm: SaleDetails, skipAllocation?: boolean) => {
      // If this is called, there are no form errors (else onSubmit is never called)
      if (sale) {
        // Check both warning conditions
        const needsSectionRowWarning =
          !showSectionRowChangeWarning &&
          (saleForm.seating.section !== sale.seating.section ||
            saleForm.seating.row !== sale.seating.row);

        const needsAdminHoldWarning =
          !showAdminHoldWarning && hasAdminHoldTickets(selectedSeatings);

        if (needsSectionRowWarning || needsAdminHoldWarning) {
          if (needsSectionRowWarning) {
            setShowSectionRowChangeWarning(true);
          }
          if (needsAdminHoldWarning) {
            setShowAdminHoldWarning(true);
          }
          return;
        }

        setIsSubmitting(true);

        tryInvokeApi(
          async () => {
            let result: PosUiActionResult;
            const client = new SaleClient(activeAccountWebClientConfig);
            if (
              sale.mkp === Marketplace.Offline &&
              (sale.status === PointOfSaleSaleStatus.Hold ||
                sale.status === PointOfSaleSaleStatus.CancelledHold ||
                sale.status === PointOfSaleSaleStatus.PendingRejection ||
                sale.status === PointOfSaleSaleStatus.Rejected)
            ) {
              // If it's offline sale and it's currently on hold or cancelled hold,
              const saleInput = getPosManagedMarketplaceSaleInputFromSale(
                sale,
                event!
              );
              result = await client.mergePosManagedMarketplaceSaleForListingId(
                saleInput.listingId,
                {
                  ...saleInput,
                  saleStatus: posChangedField(
                    PointOfSaleSaleStatus.PendingConfirmation
                  ),
                }
              );

              if (!isSuccess(result)) {
                showErrorDialog(
                  'SaleClient.mergePosManagedMarketplaceSale',
                  {
                    message: result.message ?? genericError,
                    status: result.status!,
                  } as ApiException,
                  {
                    onDismissError: () => cancelTo && setModal(cancelTo),
                    trackErrorData: {
                      item1: saleForm.id,
                      item2: saleForm.seating,
                    },
                  }
                );
                return;
              }
            }

            const skipAllocationValidation =
              activeTab === SeatingAllocationInputTab.Tickets;

            result = await client.confirmAllocationSale(
              saleForm.id,
              skipAllocation ? undefined : saleForm.seating,
              skipAllocationValidation
            );

            if (!isSuccess(result)) {
              showErrorDialog(
                'SaleClient.confirmSale',
                {
                  message: result.message ?? genericError,
                  status: result.status!,
                } as ApiException,
                {
                  onDismissError: () => cancelTo && setModal(cancelTo),
                  trackErrorData: {
                    item1: saleForm.id,
                    item2: saleForm.seating,
                  },
                }
              );
            } else {
              // Refresh the active data so the SaleDetail dialog and table will have the new content
              await setActivePosEntity(saleForm!.id, saleForm!.idOnMkp, true);
              if (cancelTo) {
                setModal(cancelTo);
              } else {
                closeModal(true);
              }
            }
          },
          (error) => {
            showErrorDialog('SaleClient.confirmSale', error, {
              trackErrorData: {
                saleId: saleForm.id,
                seation: saleForm.seating,
              },
            });
          },
          () => setIsSubmitting(false)
        );
      }

      return false;
    },
    [
      activeAccountWebClientConfig,
      activeTab,
      cancelTo,
      closeModal,
      event,
      genericError,
      hasAdminHoldTickets,
      sale,
      selectedSeatings,
      setActivePosEntity,
      setModal,
      showErrorDialog,
      showSectionRowChangeWarning,
      showAdminHoldWarning,
    ]
  );

  const updateSeatingValueFromSingleTicketSelection = useCallback(() => {
    if (activeTab === SeatingAllocationInputTab.Listings) {
      return;
    }
    const seating = createSeatingFromSelectedTickets(selectedSeatings);
    methods.setValue('seating', seating); // Update form state
  }, [activeTab, methods, selectedSeatings]);

  const contentId =
    action === SaleActionType.Confirm
      ? ContentId.ConfirmSale
      : action === SaleActionType.ConfirmAllocation
        ? ContentId.ConfirmAllocation
        : ContentId.AllocateTickets;

  return (
    <FormProvider {...methods}>
      <CancellableFormHeader cancelTo={cancelTo} disabled={isSubmitting}>
        <ConnectedEventEntityHeader title={<Content id={contentId} />} />
      </CancellableFormHeader>

      <ModalBody>
        <ModalBodyHeaderContainer>
          <Summary event={event!} posEntity={sale!} />
        </ModalBodyHeaderContainer>
        <ModalBodyDataContainer>
          {isSubmitting ? (
            <PosSpinner />
          ) : showAnyWarning ? (
            <SeatingAllocationWarning
              showAdminHoldWarning={showAdminHoldWarning}
              showAllocationChangeWarning={showSectionRowChangeWarning}
            />
          ) : (
            <SeatingAllocationInputV2
              setSelectedSeatings={setSelectedSeatings}
              selectedSeatings={selectedSeatings}
              originalSeating={sale!.seating}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              setHasAdminHoldInListingAllocation={
                setHasAdminHoldInListingAllocation
              }
            />
          )}
        </ModalBodyDataContainer>
      </ModalBody>

      <ModalFooter>
        <CancellableFormFooter
          cancelTo={cancelTo}
          disabled={isSubmitting}
          showDialogOnCancel={isDirty}
        >
          {showAnyWarning && (
            <ConfirmButton
              onClick={() => {
                setShowSectionRowChangeWarning(false);
                setShowAdminHoldWarning(false);
              }}
              disabled={isSubmitting}
              textContentId={ContentId.Back}
            />
          )}
          {action === SaleActionType.Confirm &&
            sale?.isSeatSaver &&
            !seating?.ticketIds?.length && (
              <ConfirmButton
                onClick={methods.handleSubmit((f) => onSubmit(f, true))}
                disabled={isSubmitting}
                variant="outline"
                textContentId={ContentId.ConfirmWithoutAllocation}
              />
            )}
          <ConfirmButton
            onClick={() => {
              updateSeatingValueFromSingleTicketSelection();
              methods.handleSubmit((f) => onSubmit(f))();
            }}
            disabled={
              isSubmitting ||
              (activeTab === SeatingAllocationInputTab.Tickets
                ? selectedSeatings.length < (sale?.qtySold || 0)
                : !seating?.ticketIds?.length)
            }
            textContentId={
              showAnyWarning ? ContentId.Yes : ContentId.ConfirmAllocation
            }
          />
        </CancellableFormFooter>
      </ModalFooter>
    </FormProvider>
  );
};

const SeatingAllocationWarning = ({
  showAllocationChangeWarning,
  showAdminHoldWarning,
}: {
  showAllocationChangeWarning: boolean;
  showAdminHoldWarning: boolean;
}) => {
  return (
    <div>
      <h5>
        <Content id={ContentId.AreYouSure} />
      </h5>
      {showAllocationChangeWarning && (
        <WarningMessage
          message={<Content id={ContentId.SeatAllocationChangeWarning} />}
        />
      )}
      {showAdminHoldWarning && (
        <WarningMessage
          message={
            <Content id={ContentId.SeatAllocationListingAdminHoldWarning} />
          }
        />
      )}
    </div>
  );
};
